import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { TooltipModule } from 'primeng/tooltip';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TagModule } from 'primeng/tag';
import { NotificationService } from '../../../core/services/notifications/notification.service';
import { PecReceiptData, TipoRicevutaPec, WebSocketStatus } from '../../../core/models/pec/pec-notification.model';
import { MaterialIconComponent } from '../material-icon/material-icon.component';

/**
 * Componente per visualizzare il pannello delle notifiche PEC
 *
 * Features:
 * - Lista delle ricevute PEC in tempo reale
 * - Indicatore dello stato della connessione
 * - Badge con conteggio notifiche non lette
 * - Design moderno e professionale
 */
@Component({
  selector: 'app-pec-notification-panel',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    BadgeModule,
    TooltipModule,
    ScrollPanelModule,
    TagModule,
    MaterialIconComponent
  ],
  templateUrl: './pec-notification-panel.component.html',
  styleUrl: './pec-notification-panel.component.scss'
})
export class PecNotificationPanelComponent implements OnInit, OnDestroy {

  // Visibilità del pannello (two-way binding)
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // Ricevute PEC
  receipts: PecReceiptData[] = [];
  unreadCount = 0;

  // Stato connessione
  connectionStatus: WebSocketStatus = WebSocketStatus.DISCONNECTED;
  isConnected = false;

  // Enum per il template
  WebSocketStatus = WebSocketStatus;
  TipoRicevutaPec = TipoRicevutaPec;

  private destroy$ = new Subject<void>();

  constructor(
    private pecNotificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Sottoscrivi alle ricevute
    this.pecNotificationService.getReceipts()
      .pipe(takeUntil(this.destroy$))
      .subscribe(receipts => {
        this.receipts = receipts;
        this.unreadCount = receipts.length;
      });

    // Sottoscrivi allo stato della connessione
    this.pecNotificationService.getConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.connectionStatus = status;
        this.isConnected = status === WebSocketStatus.CONNECTED;
      });
  }

  /**
   * Apre il pannello delle notifiche
   */
  openPanel(): void {
    this.visible = true;
    this.unreadCount = 0;
  }

  /**
   * Chiude il pannello delle notifiche
   */
  closePanel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /**
   * Pulisce tutte le ricevute
   */
  clearAll(): void {
    this.pecNotificationService.clearReceipts();
  }

  /**
   * Ottiene il colore del tag in base al tipo di ricevuta
   */
  getTagSeverity(tipo: TipoRicevutaPec): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
    switch (tipo) {
      case TipoRicevutaPec.CONSEGNA:
      case TipoRicevutaPec.AVVENUTA_CONSEGNA:
        return 'success';
      case TipoRicevutaPec.ACCETTAZIONE:
      case TipoRicevutaPec.PRESA_IN_CARICO:
        return 'info';
      case TipoRicevutaPec.ERRORE_CONSEGNA:
      case TipoRicevutaPec.MANCATA_CONSEGNA:
        return 'danger';
      default:
        return 'warning';
    }
  }

  /**
   * Ottiene l'icona in base al tipo di ricevuta
   */
  getReceiptIcon(tipo: TipoRicevutaPec): string {
    switch (tipo) {
      case TipoRicevutaPec.CONSEGNA:
      case TipoRicevutaPec.AVVENUTA_CONSEGNA:
        return 'mark_email_read';
      case TipoRicevutaPec.ACCETTAZIONE:
      case TipoRicevutaPec.PRESA_IN_CARICO:
        return 'mail';
      case TipoRicevutaPec.ERRORE_CONSEGNA:
      case TipoRicevutaPec.MANCATA_CONSEGNA:
        return 'error';
      default:
        return 'info';
    }
  }

  /**
   * Ottiene il colore dell'icona in base al tipo di ricevuta
   */
  getReceiptIconColor(tipo: TipoRicevutaPec): string {
    switch (tipo) {
      case TipoRicevutaPec.CONSEGNA:
      case TipoRicevutaPec.AVVENUTA_CONSEGNA:
        return '#4caf50';
      case TipoRicevutaPec.ACCETTAZIONE:
      case TipoRicevutaPec.PRESA_IN_CARICO:
        return '#2196f3';
      case TipoRicevutaPec.ERRORE_CONSEGNA:
      case TipoRicevutaPec.MANCATA_CONSEGNA:
        return '#f44336';
      default:
        return '#ff9800';
    }
  }

  /**
   * Ottiene il label per lo stato della connessione
   */
  getConnectionStatusLabel(): string {
    switch (this.connectionStatus) {
      case WebSocketStatus.CONNECTED:
        return 'Connesso';
      case WebSocketStatus.CONNECTING:
        return 'Connessione in corso...';
      case WebSocketStatus.DISCONNECTED:
        return 'Disconnesso';
      case WebSocketStatus.RECONNECTING:
        return 'Riconnessione...';
      case WebSocketStatus.ERROR:
        return 'Errore';
      default:
        return 'Sconosciuto';
    }
  }

  /**
   * Ottiene il colore per lo stato della connessione
   */
  getConnectionStatusColor(): string {
    switch (this.connectionStatus) {
      case WebSocketStatus.CONNECTED:
        return '#4caf50';
      case WebSocketStatus.CONNECTING:
      case WebSocketStatus.RECONNECTING:
        return '#ff9800';
      case WebSocketStatus.DISCONNECTED:
      case WebSocketStatus.ERROR:
        return '#f44336';
      default:
        return '#757575';
    }
  }

  /**
   * Formatta la data in modo leggibile
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;

    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

